import { ApolloCache } from "@apollo/client"

import { evict } from "shared/api/evict"
import { forEachNode } from "shared/lib/treeUtils"
import {
  FloorPriceTypeName,
  ProductPositionPriceTypeName,
  ProjectPriceTypename,
  RoomPriceTypeName
} from "shared/types/Price"
import {
  FloorTypename,
  ProductPositionTypename,
  ProjectChild,
  ProjectTypename,
  RoomTypename,
  isProjectFloor,
  isProjectProductPosition,
  isProjectRoom
} from "shared/types/Project"
import {
  ProjectsAPIDeleteRequestContext,
  ProjectsAPIPositionUpdateRequestContext
} from "shared/types/ProjectMutations"

import { findParentRooms } from "entities/project-children/lib/findProjectChildParent"
import { getProjectChildren } from "entities/project-children/lib/getProjectChildren"

export function evictProductPositionPrice(
  productPositionID: string,
  cache: ApolloCache<object>
) {
  evict({
    cache,
    id: productPositionID,
    typename: ProductPositionPriceTypeName
  })
}

export function evictRoomPrice(roomID: string, cache: ApolloCache<object>) {
  evict({
    cache,
    id: roomID,
    typename: RoomPriceTypeName
  })
}

export function evictFloorPrice(floorID: string, cache: ApolloCache<object>) {
  evict({
    cache,
    id: floorID,
    typename: FloorPriceTypeName
  })
}

export function evictChildrenPriceCache(
  children: ProjectChild[],
  cache: ApolloCache<object>
) {
  children.forEach(child => {
    evictProjectChildPrice(child, cache)
    if (isProjectRoom(child) || isProjectFloor(child)) {
      evictChildrenPriceCache(child.children ?? [], cache)
    }
  })
}

export function evictProjectChildPrice(
  child: ProjectChild,
  cache: ApolloCache<object>
) {
  if (isProjectProductPosition(child)) {
    evictProductPositionPrice(child.id, cache)
  }
  if (isProjectRoom(child)) {
    evictRoomPrice(child.id, cache)
  }
  if (isProjectFloor(child)) {
    evictFloorPrice(child.id, cache)
  }
}

export function evictProjectPrice(
  projectID: string,
  cache: ApolloCache<object>,
  includeChildren?: boolean
) {
  evict({
    cache,
    id: projectID,
    typename: ProjectPriceTypename
  })

  function invalidateChild(node: ProjectChild) {
    evictProjectChildPrice(node, cache)
  }

  if (includeChildren) {
    const projectChildren: ProjectChild[] | null | undefined =
      getProjectChildren(projectID, ProjectTypename, cache)

    forEachNode(projectChildren, invalidateChild)
  }
}

export function evictParentPricesAfterPositionUpdate({
  cache,
  context
}: {
  cache: ApolloCache<object>
  context: ProjectsAPIPositionUpdateRequestContext
}) {
  const {
    projectID,
    prevParentID,
    prevParentType,
    nextParentID,
    nextParentType
  } = context

  if (prevParentID && prevParentType === RoomTypename) {
    const prevParents = findParentRooms({
      projectID,
      cache,
      childID: prevParentID
    })
    evictRoomPrice(prevParentID, cache)
    prevParents?.forEach(parent => evictProjectChildPrice(parent, cache))
  }
  if (prevParentID && prevParentType === FloorTypename) {
    const prevParents = findParentRooms({
      projectID,
      cache,
      childID: prevParentID
    })
    evictFloorPrice(prevParentID, cache)
    prevParents?.forEach(parent => evictProjectChildPrice(parent, cache))
  }
  if (nextParentID && nextParentType === RoomTypename) {
    const nextParents = findParentRooms({
      projectID,
      cache,
      childID: nextParentID
    })
    evictRoomPrice(nextParentID, cache)
    nextParents?.forEach(parent => evictProjectChildPrice(parent, cache))
  }
  if (nextParentID && nextParentType === FloorTypename) {
    const nextParents = findParentRooms({
      projectID,
      cache,
      childID: nextParentID
    })
    evictFloorPrice(nextParentID, cache)
    nextParents?.forEach(parent => evictProjectChildPrice(parent, cache))
  }
}

export function evictAffectedPrices({
  projectID,
  itemID,
  itemType,
  cache
}: {
  projectID: string
  itemID: string
  itemType:
    | typeof ProductPositionTypename
    | typeof RoomTypename
    | typeof FloorTypename
    | typeof ProjectTypename
  cache: ApolloCache<object>
}) {
  evictProjectPrice(projectID, cache)

  if (itemType === ProductPositionTypename) {
    evictProductPositionPrice(itemID, cache)
  }
  if (itemType === RoomTypename) {
    evictRoomPrice(itemID, cache)
  }
  if (itemType === FloorTypename) {
    evictFloorPrice(itemID, cache)
  }

  if (itemID !== projectID) {
    const parents = findParentRooms({ projectID, childID: itemID, cache })

    if (parents) {
      parents.forEach(parent =>
        evictProjectChildPrice(parent as ProjectChild, cache)
      )
    }
  }
}

export function evictParentPricesAfterDeletion({
  cache,
  context
}: {
  cache: ApolloCache<object>
  context: ProjectsAPIDeleteRequestContext
}) {
  const { projectID, parentType, parentID } = context

  evictAffectedPrices({
    projectID,
    itemID: parentID,
    itemType: parentType,
    cache
  })
}

export function evictPricesByProductType({
  projectID,
  productType,
  cache
}: {
  projectID: string
  productType: string
  cache: ApolloCache<object>
}) {
  evictProjectPrice(projectID, cache)

  const projectChildren = getProjectChildren(projectID, ProjectTypename, cache)

  function invalidate(child: ProjectChild) {
    const projectChild = child
    if (
      isProjectProductPosition(projectChild) &&
      projectChild.variantKey.split("-")[0] === productType
    ) {
      evictProductPositionPrice(projectChild.id, cache)
    }
    if (isProjectRoom(projectChild)) {
      evictRoomPrice(projectChild.id, cache)
    }
    if (isProjectFloor(projectChild)) {
      evictFloorPrice(projectChild.id, cache)
    }
  }

  forEachNode(projectChildren, invalidate)
}
